import * as React from "react";
import { Context } from "../appcontext";
import { ElementHtmlAttributes, getAttributes } from "../common/common";
import {
  BackgroundColorMode,
  cJSonFunctionExecuteShortcut,
  CondFormattingStyle,
  CSUpdateInput,
  FrgtInputData,
  K2Boolean,
  TAcceptMode,
  TColorShiftLevel,
  TColorType,
  TCondFormattingOption,
  TInputMultiLineMode,
  TNclInputType,
  TUFDisplayValueAs,
  UpdateControl,
  UpdateInput,
} from "../common/communication.base";
import { NclInput, EditCheckValueResult, NclLocatorPanel, InputSelectionText, UFOverAlign, UFOverAlignListener } from "../common/components.ncl";
import { AcquireControl, K2ComponentState, StyleHelper, withContext, WithContextPlacementProps } from "./k2hoc";
import { K2Img } from "./K2Image";
import { getTextDecoration, K2RuleWithVCXProps } from "./k2StyleRenderer";
import { K2ToolBar } from "./K2ToolBar";
import { K2TruncateText } from "./K2TruncateText";
import { setRequestedActiveControl } from "./app";
import { VCXColorMap, VisualContext } from "../common/visualContext";
import { RealizerOperations, ViewRealizerManager } from "../viewrealizer";

interface InputHtmlAttributes extends ElementHtmlAttributes {
  type: string;
  id: string;

  maxLength?: number;
  autoComplete?: string;
  placeholder?: string;
  pattern?: string;
  title?: string;

  min?: number;
  max?: number;
  step?: number;
}

/* CSS */

const inputRule = (props: K2RuleWithVCXProps) => ({
  display: "flex",
  flexDirection: "column" as "column",
});

export const inputTag = (props: K2RuleWithVCXProps) => ({
  background: "transparent",
  border: "none",
  flex: 1,
  width: "auto",
  paddingLeft: ".2em",
  minWidth: props.vcx.sizeMap(20) + "px",
  userSelect: "auto" as "auto",
});

const prefixAndSufix = (props: K2RuleWithVCXProps) => ({
  padding: "0px " + props.vcx.sizeMap(3) + "px",
  color: props.vcx.getColor(props.vcx.Data.ColorMap.ContentDecorateColorFrg),
  width: "auto",
  overflow: "hidden",
  flex: "0 0 auto",
  display: "flex",
  alignItems: "center",
});

const toolbarRule = (props: K2RuleWithVCXProps) => ({
  flex: "1 0 auto",
  alignSelf: "center",
  color: props.vcx.getColor(props.vcx.Data.ColorMap.BaseColorBck1),
});

const alignLeft = (props: K2RuleWithVCXProps) => ({
  textAlign: "left" as "left",
});

const alignRight = (props: K2RuleWithVCXProps) => ({
  textAlign: "right" as "right",
});

export const inputContainerRule = (props: K2RuleWithVCXProps) => ({
  flex: "1 1 auto",
  width: "100%",
  overflow: "hidden",
});

export const innerInputRule = (props: K2RuleWithVCXProps & { formatting: CondFormattingStyle }) => ({
  backgroundColor: ColumnBaseHelper.getBackgroundColor(props.formatting, false, false, props.vcx),
  color: ColumnBaseHelper.getForegroundColor(props.formatting, false, false, props.vcx),
  fontWeight: ColumnBaseHelper.IsBold(props.formatting) ? ("bold" as "bold") : ("normal" as "normal"),
  fontStyle: ColumnBaseHelper.IsItalic(props.formatting) ? ("italic" as "italic") : ("normal" as "normal"),
  textDecoration: getTextDecoration(ColumnBaseHelper.IsStrike(props.formatting), ColumnBaseHelper.IsUnderline(props.formatting)),
});

interface InputState extends K2ComponentState<UpdateInput> {
  Focused: boolean;
  revealPassword: boolean;
}

enum TextPosition {
  beforeCursor,
  afterCursor,
  selected,
}

interface InputProps extends WithContextPlacementProps {
  blockSoftKeyboard?: boolean;
}

export class _Input extends React.PureComponent<InputProps, InputState> implements UFOverAlign {
  static displayName = `K2Input`;
  private element: InnerInput | InnerSimpleInput;
  private timer: any;
  private rootEl: HTMLElement;
  private listener: UFOverAlignListener;
  private control: NclInput;
  private requestSelectionStart: number;

  constructor(props: InputProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclInput;
    }) as NclInput;
    this.state = { data: this.control.init(this) as UpdateInput, Focused: false, vcxVersion: -1, revealPassword: false };
  }

  registerOverAlignListener(listener: UFOverAlignListener): void {
    this.listener = listener;
  }

  unRegisterOverAlignListener(listener: UFOverAlignListener): void {
    if (this.listener === listener) {
      this.listener = null;
    }
  }

  getOverRect(): DOMRect {
    let result = undefined;
    if (this.element) {
      result = this.element.getOverRect();
    }

    if (!result) {
      if (this.rootEl) {
        result = this.rootEl.getBoundingClientRect();
      }
    }

    return result;
  }

  componentWillUnmount() {
    this.control.willUnMount(true);
    this.control = null;
  }

  updateState(state: UpdateControl) {
    this.setState((prevState: K2ComponentState<UpdateInput>) => {
      return { data: state as UpdateInput };
    });
  }

  updateVCX(vcxVersion: number) {
    this.setState({ vcxVersion: vcxVersion });
  }

  setAsActive(isActive: boolean) {
    if (this.element && isActive) {
      this.element.focus();
    }
  }

  componentDidUpdate(prevProps: WithContextPlacementProps, prevState: InputState) {
    if (this.state.data != prevState.data) {
      if (this.element) {
        if (this.requestSelectionStart >= 0) {
          this.element.updateState(this.state.data, { Value: this.getText(), Position: this.requestSelectionStart });
        } else {
          this.element.updateState(this.state.data, { Value: this.getText() });
        }
      }

      if (prevState.data.Visible !== true && this.state.data.Visible === true) {
        if (this.listener) {
          this.listener.updateAlignAnchor();
        }
      }
    }
  }

  public getSelectionStart(): number {
    if (this.element) return this.element.selectionStart;
    return -1;
  }

  public getSelectionEnd(): number {
    if (this.element) return this.element.selectionEnd;
    return -1;
  }

  public setText(value: string, position: number) {
    this.requestSelectionStart = position;
    this.control.updateState({ Text: value } as CSUpdateInput);
  }

  render() {
    return (
      <div
        data-k2-errors-count={this.state.data.ErrorsCount > 0 ? this.state.data.ErrorsCount : undefined}
        style={StyleHelper(this.control, { ...this.props.style, padding: 0 })}
        ref={(ref) => {
          this.rootEl = ref;
        }}
        className={this.props.renderer.renderRule(inputRule, { vcx: this.control.VCX })}
      >
        <K2LabelInput
          titleVisible={this.control.isVisibleTitle()}
          title={this.state.data.Title}
          errorsCount={this.state.data.ErrorsCount}
          warningsCount={this.state.data.WarningsCount}
          focused={this.state.Focused}
          inEditMode={this.control.InEditMode}
          modified={this.state.data.Modified}
          readOnly={this.state.data.ReadOnly}
          vcx={this.control.VCX}
          frgtData={this.control.Ncl.FrgtData}
          id={this.control.Ncl.Name}
        >
          {this.getInputHtml()}
        </K2LabelInput>
      </div>
    );
  }

  protected getText(): string {
    let newText = this.state.data.Text;
    if (this.state.data.PasteText && this.state.data.PasteText.length > 0) {
      if (this.element) {
        if (this.element.selectionStart >= 0 && this.element.selectionStart < newText.length) {
          if (this.element.selectionEnd >= 0 && this.element.selectionEnd < newText.length) {
            newText = newText.substring(0, this.element.selectionStart) + this.state.data.PasteText + newText.substring(this.element.selectionEnd);
          } else {
            newText = newText.substring(0, this.element.selectionStart) + this.state.data.PasteText + newText.substring(this.element.selectionStart);
          }
        } else {
          newText = newText + this.state.data.PasteText;
        }
      } else {
        newText = newText + this.state.data.PasteText;
      }
    }

    return newText;
  }

  private getHorizontalAlignByTyp(InputType: number): string {
    if (InputType === TNclInputType.nitString) {
      return this.props.renderer.renderRule(alignLeft, { vcx: this.control.VCX });
    }
    return this.props.renderer.renderRule(alignRight, { vcx: this.control.VCX });
  }

  private getPrefixSufix(inputClassFont: string, isPrefix: boolean = true): JSX.Element {
    let glyphId: string = "";
    let text: string = "";
    let valAs: TUFDisplayValueAs;
    if (isPrefix) {
      glyphId = this.state.data.PrefixGlyphId;
      text = this.state.data.PrefixText;
      valAs = this.control.Ncl.PrefixDisplayAs;
    } else {
      glyphId = this.state.data.SuffixGlyphId;
      text = this.state.data.SuffixText;
      valAs = this.control.Ncl.SuffixDisplayAs;
    }
    switch (valAs) {
      case TUFDisplayValueAs.dvaImage:
        return (
          <K2Img
            glyphId={glyphId}
            vcx={this.control.VCX}
            strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorBck1)}
            width={this.control.VCX.InputControl.getEditHeight(1)}
            height={this.control.VCX.InputControl.getEditHeight(1)}
          />
        );
      case TUFDisplayValueAs.dvaText:
        return <span className={inputClassFont + this.props.renderer.renderRule(prefixAndSufix, { vcx: this.control.VCX })}>{text}</span>;
      default:
        return null;
    }
  }

  private getTextFromPosition(option: TextPosition): string {
    if (
      this.element &&
      this.element.Value &&
      this.element.selectionStart &&
      this.element.selectionStart > 0 &&
      this.element.selectionStart < this.element.Value.length
    ) {
      switch (option) {
        case TextPosition.beforeCursor:
          return this.element.Value[this.element.selectionStart - 1];
        case TextPosition.afterCursor:
          return this.element.Value[this.element.selectionStart];
        case TextPosition.selected:
          if (this.element.selectionEnd && this.element.selectionStart < this.element.selectionEnd) {
            return this.element.Value.slice(this.element.selectionStart, this.element.selectionEnd);
          }
        default:
          break;
      }
    }

    return "";
  }

  private getInputHtml(): JSX.Element {
    let inputCss: string =
      this.props.renderer.renderFontRule(this.control.VCX.InputControl.InputFont, this.control.VCX) +
      " " +
      this.props.renderer.renderRule(innerInputRule, { vcx: this.control.VCX, formatting: this.state.data.FormattingStyle });

    return (
      <div style={{ flex: "1 1 auto" }} onContextMenu={Context.DeviceInfo.IndependentFormatMode ? null : this.handleContextMenu}>
        <div className={this.props.renderer.renderRule(inputContainerRule, { vcx: this.control.VCX })}>
          {this.getPrefixSufix(inputCss, true)}
          {this.getInputTag(inputCss)}
          {this.getPrefixSufix(inputCss, false)}
        </div>
        {this.control.ToolBar && (
          <K2ToolBar
            controlUID={this.control.ToolBar.MetaData.ControlUID}
            vrUID={this.props.vrUID}
            className={this.props.renderer.renderRule(toolbarRule, { vcx: this.control.VCX })}
            style={{ width: "auto" }}
          />
        )}
        {this.control.Ncl.FrgtData.IsPassword === 1 && (
          <div style={{ flex: "0 0 auto" }}>
            <button type="button" style={{ border: "none", backgroundColor: "transparent" }} onClick={this.revealPassword}>
              <K2Img
                width={this.control.VCX.MinRowHeight}
                height={this.control.VCX.MinRowHeight}
                glyphId={this.state.revealPassword ? "wui*eye.no" : "wui*eye"}
                vcx={this.control.VCX}
                strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorBck1)}
              />
            </button>
          </div>
        )}
      </div>
    );
  }

  private revealPassword = () => {
    this.setState({ revealPassword: !this.state.revealPassword });
  };

  private refCallBack = (element: InnerInput | InnerSimpleInput) => {
    if (element) {
      this.element = element;
    }
  };

  private getInputTag(inputCss: string): JSX.Element {
    if (Context.DeviceInfo.IndependentFormatMode) {
      return (
        <K2InnerSimpleInput
          controlUID={this.control.MetaData.ControlUID}
          vrUID={this.control.getRealizerUID()}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onKeyPress={this.handleKeyPress}
          change={this.change}
          onDrop={this.handleDrop}
          onPaste={this.handlePaste}
          showContextMenu={() => {
            this.showContextMenu();
          }}
          ref={this.refCallBack}
          InputTagPlaceholder={this.state.data.Watermark}
          className={this.getHorizontalAlignByTyp(this.control.Ncl.InputType) + " " + inputCss}
          revealPassword={this.state.revealPassword}
          blockSoftKeyboard={this.props.blockSoftKeyboard}
        />
      );
    } else {
      return (
        <K2InnerInput
          controlUID={this.control.MetaData.ControlUID}
          vrUID={this.control.getRealizerUID()}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          change={this.change}
          onDrop={this.handleDrop}
          onKeyDown={this.handleKeyDown}
          onKeyPress={this.handleKeyPress}
          onPaste={this.handlePaste}
          ref={this.refCallBack}
          InputTagPlaceholder={this.state.data.Watermark}
          className={this.getHorizontalAlignByTyp(this.control.Ncl.InputType) + " " + inputCss}
          revealPassword={this.state.revealPassword}
        />
      );
    }
  }

  private change = (value: string) => {
    this.control.change(value, false);
    switch (this.state.data.AcceptMode) {
      case TAcceptMode.amImmediate:
        this.control.accept();
        break;
      case TAcceptMode.amDelayed:
        if (this.timer) {
          clearTimeout(this.timer);
          this.timer = null;
        }
        this.timer = setTimeout(() => {
          this.control.accept();
        }, 1000);
        break;
    }
  };

  private handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if ((this.control.State as UpdateInput).ReadOnly) return;
    this.control.setActiveControlRequested();
    this.setState({ Focused: true });
    e.stopPropagation();
  };

  private handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    this.setState({ Focused: false });
  };

  private handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    this.showContextMenu();
    return false;
  };

  private showContextMenu() {
    this.control.contextMenu();
  }

  public handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
    if (e.keyCode === 8 || e.keyCode === 46) {
      if (this.control.isDateOrTime()) {
        let value: string = this.getTextFromPosition(TextPosition.selected);
        if (!value) {
          value = this.getTextFromPosition(e.keyCode === 8 ? TextPosition.beforeCursor : TextPosition.afterCursor);
        }
        if (value && value.indexOf(Context.getApplication().getLocalSeparator(this.control.Ncl.InputType)) >= 0) {
          e.preventDefault();
          e.stopPropagation();
          return;
        }
      }
    }
  };

  public handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
    if (this.checkInput(e.key) > EditCheckValueResult.default) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
  };

  private handlePaste = (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (this.checkInput(e.clipboardData.getData("text").trim()) > EditCheckValueResult.default) {
      e.preventDefault();
      return;
    }
  };

  private handleDrop = (e: React.DragEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let data = e.dataTransfer.getData("text").trim();
    let value = this.checkInput(data, true);
    switch (value) {
      case EditCheckValueResult.block:
        e.preventDefault();
        break;
      case EditCheckValueResult.default:
        this.element.updateState(null, { Value: data });
        e.preventDefault();
      default:
        this.element.focus();
        break;
    }
  };

  private checkInput(key: string, replaceAll?: boolean): EditCheckValueResult {
    if ((this.control.State as UpdateInput).ReadOnly) return;
    if (!this.element) return EditCheckValueResult.default;
    if (this.element.selectionStart && this.element.selectionStart < 0 && !replaceAll) return EditCheckValueResult.default;
    let selection: InputSelectionText = {
      start: this.element.selectionStart !== null && this.element.selectionStart >= 0 ? this.element.selectionStart : this.element.Value.length,
      end: this.element.selectionEnd !== null && this.element.selectionEnd >= 0 ? this.element.selectionEnd : this.element.Value.length,
    };
    let result = this.control.checkInput(this.element.Value, key, selection, replaceAll);
    switch (result.Result) {
      case EditCheckValueResult.block:
        //beep???
        break;
      case EditCheckValueResult.newValue:
        this.element.updateState(null, { Value: result.Value as string, Position: result.ToPosition });
        break;
      default:
        break;
    }

    return result.Result;
  }
}

const labelStyles = (props: K2RuleWithVCXProps & { labelBgColor: string; labelFontColor: string }) => ({
  padding: `0px ${props.vcx.sizeMap(8)}px`,
  background: props.labelBgColor,
  color: props.labelFontColor,
});

const inputStyles = (
  props: K2RuleWithVCXProps & { borderColor: string; inputBgColor: string; inputFontColor: string; focused: boolean; showFrame: K2Boolean; readOnly: boolean }
) => ({
  flex: "1 1 auto",
  border: props.showFrame
    ? props.focused
      ? `${props.vcx.InputControl.getInputFrameWidth() * 2}px solid ${props.vcx.getColor(props.vcx.Data.ColorMap.ContentDecorateColorFrg)}`
      : `${props.vcx.InputControl.getInputFrameWidth()}px solid ${props.borderColor}`
    : null,
  background: props.inputBgColor,
  color: props.inputFontColor,
  padding: props.showFrame ? (props.focused ? 0 : props.vcx.InputControl.getInputFrameWidth() + "px") : 0 + "px",
  ":hover": {
    border: props.showFrame && !props.readOnly ? `${props.vcx.InputControl.getInputFrameWidth() * 2}px solid ${props.borderColor}` : null,
    padding: props.showFrame && !props.readOnly ? 0 : null,
  },
});

interface LabelInputProps extends WithContextPlacementProps {
  titleVisible: boolean;
  title: string;
  inEditMode: boolean;
  readOnly: boolean;
  vcx: VisualContext;
  focused: boolean;
  errorsCount: number;
  warningsCount: number;
  modified: boolean;
  frgtData: FrgtInputData;
  id: string;
}

class _K2LabelInput extends React.Component<LabelInputProps> {
  constructor(props: LabelInputProps) {
    super(props);
  }

  getHColor = (inv: boolean, colorType: number) => {
    let result: string;
    let i = Math.floor(colorType / 2);

    if (!inv) {
      i += 1;
    }

    result = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.HighlightColorSet[`Color${i}`] as number);

    return result;
  };

  getColor = (bgColorMode: BackgroundColorMode) => {
    let bgColor: string;
    let {
      ctHColor1,
      ctHColor2,
      ctHColor3,
      ctHColor4,
      ctHColor5,
      ctHColor6,
      ctHColor7,
      ctHColor8,
      ctHColor1Inv,
      ctHColor2Inv,
      ctHColor3Inv,
      ctHColor4Inv,
      ctHColor5Inv,
      ctHColor6Inv,
      ctHColor7Inv,
      ctHColor8Inv,
      ctError,
      ctErrorInv,
      ctWarning,
      ctWarningInv,
      ctHighlight,
      ctHighlightInv,
    } = TColorType;

    switch (bgColorMode.ColorType) {
      case ctHColor1:
      case ctHColor2:
      case ctHColor3:
      case ctHColor4:
      case ctHColor5:
      case ctHColor6:
      case ctHColor7:
      case ctHColor8:
        bgColor = this.getHColor(false, bgColorMode.ColorType);
        break;
      case ctHColor1Inv:
      case ctHColor2Inv:
      case ctHColor3Inv:
      case ctHColor4Inv:
      case ctHColor5Inv:
      case ctHColor6Inv:
      case ctHColor7Inv:
      case ctHColor8Inv:
        bgColor = this.props.vcx.getColor(VCXColorMap.getDifferentColor(parseInt(this.getHColor(true, bgColorMode.ColorType)), TColorShiftLevel.csl3));
        break;
      case ctError:
        bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ErrorColorBck);
        break;
      case ctErrorInv:
        bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ErrorColorFrg);
        break;
      case ctWarning:
        bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.WarningColorBck);
        break;
      case ctWarningInv:
        bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.WarningColorFrg);
        break;
      case ctHighlight:
        bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.HighLightColorBck);
        break;
      case ctHighlightInv:
        bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.HighLightColorFrg);
        break;
      default:
        bgColor = this.props.vcx.getColor(bgColorMode.CustomColor);
        break;
    }

    return bgColor;
  };

  editBgColor = () => {
    let bgColor: string;

    if (this.props.frgtData.BackgroundColorEditMode.ColorType !== 0) {
      bgColor = this.getColor(this.props.frgtData.BackgroundColorEditMode);
    } else {
      bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeColorBck);
    }

    return bgColor;
  };

  browseBgColor = () => {
    let bgColor: string;

    if (this.props.frgtData.BackgroundColorReadMode.ColorType !== 0) {
      bgColor = this.getColor(this.props.frgtData.BackgroundColorReadMode);
    } else {
      bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataBrowseColorBck);
    }

    return bgColor;
  };

  readonlyBackColor = () => {
    let bgColor: string;

    if (this.props.frgtData.BackgroundColorDisabledMode.ColorType !== 0) {
      bgColor = this.getColor(this.props.frgtData.BackgroundColorDisabledMode);
    } else {
      bgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeROColorBck);
    }

    return bgColor;
  };

  resolveColors = () => {
    let labelBgColor: string;
    let labelFontColor: string;
    let borderColor: string;
    let inputBgColor: string;
    let inputFontColor: string;

    if (this.props.inEditMode || !this.props.readOnly) {
      if (this.props.readOnly) {
        labelBgColor = "inherit";
        labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentDecorateColorFrg);
        borderColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentFrame1ForChange);
        inputFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeROColorFrg);
        inputBgColor = this.readonlyBackColor();
      } else {
        if (this.props.focused) {
          borderColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentDecorateColorFrg);
        } else {
          borderColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentFrame3ForChange);
        }

        if (this.props.errorsCount > 0) {
          labelBgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ErrorColorBck);
          labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ErrorColorFrg);
          inputFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeColorFrg);
          inputBgColor = this.editBgColor();
        } else if (this.props.warningsCount > 0) {
          labelBgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.WarningColorBck);
          labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.WarningColorFrg);
          inputFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeColorFrg);
          inputBgColor = this.editBgColor();
        } else if (this.props.modified) {
          labelBgColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentChangeDecorateColorFrg);
          labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentColorBck1);
          inputFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeColorFrg);
          inputBgColor = this.editBgColor();
        } else {
          labelBgColor = "inherit";
          labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentDecorateColorFrg);
          inputFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataChangeColorFrg);
          inputBgColor = this.editBgColor();
        }
      }
    } else {
      labelBgColor = "inherit";
      labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentDecorateColorFrg);
      borderColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentFrame2);
      inputFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.DataBrowseColorFrg);
      inputBgColor = this.browseBgColor();
    }

    if (this.props.frgtData.DisableLabelHighlight) {
      labelBgColor = "inherit";
      labelFontColor = this.props.vcx.getColor(this.props.vcx.Data.ColorMap.ContentDecorateColorFrg);
    }

    return [labelBgColor, labelFontColor, borderColor, inputBgColor, inputFontColor];
  };

  render() {
    let [labelBgColor, labelFontColor, borderColor, inputBgColor, inputFontColor] = this.resolveColors();

    return (
      <>
        {this.props.titleVisible && (
          <div style={{ flex: "0 0 auto" }}>
            <label htmlFor={this.props.id}>
              <K2TruncateText
                className={
                  this.props.renderer.renderRule(labelStyles, { vcx: this.props.vcx, labelBgColor: labelBgColor, labelFontColor: labelFontColor }) +
                  " " +
                  this.props.renderer.renderFontRule(this.props.vcx.InputControl.LabelFont, this.props.vcx)
                }
              >
                {this.props.title === "" ? " " : this.props.title}
              </K2TruncateText>
            </label>
          </div>
        )}
        <div
          className={this.props.renderer.renderRule(inputStyles, {
            vcx: this.props.vcx,
            borderColor: borderColor,
            inputBgColor: inputBgColor,
            inputFontColor: inputFontColor,
            inEditMode: this.props.inEditMode,
            focused: this.props.focused,
            showFrame: this.props.frgtData.ShowFrame,
            readOnly: this.props.readOnly,
          })}
        >
          <div style={{ padding: this.props.vcx.InputControl.getInputInteriorBorder() + "px", flex: "1 1 auto" }}>{this.props.children}</div>
        </div>
      </>
    );
  }
}

export const K2LabelInput = withContext(_K2LabelInput);

interface InnerInputProps extends WithContextPlacementProps {
  onFocus: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyPress?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onPaste?: (e: React.ClipboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onDrop?: (e: React.DragEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  change: (value: string) => void;
  InputTagPlaceholder: string;
  revealPassword: boolean;
}

interface InnerInputBaseState {
  Value?: string;
  Position?: number;
}

interface InnerInputStateBase extends K2ComponentState<UpdateInput>, InnerInputBaseState {}

interface InnerInputState extends InnerInputStateBase {}

class InnerInput extends React.PureComponent<InnerInputProps, InnerInputState> {
  static displayName = `InnerInput`;
  private element: HTMLInputElement | HTMLTextAreaElement;
  private control: NclInput;

  constructor(props: InnerInputProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclInput;
    }) as NclInput;

    this.state = { data: this.control.State as UpdateInput, Value: "", vcxVersion: -1 };
  }

  public get selectionStart(): number {
    return this.element ? this.element.selectionStart : -1;
  }

  public set selectionStart(value: number) {
    if (this.element) this.element.selectionStart = value;
  }

  public get selectionEnd(): number {
    return this.element ? this.element.selectionEnd : -1;
  }

  public set selectionEnd(value: number) {
    if (this.element) this.element.selectionStart = value;
  }

  getOverRect(): DOMRect {
    if (this.element) {
      return this.element.getBoundingClientRect();
    }

    return null;
  }

  public get Value(): string {
    return this.state.Value;
  }

  updateState(state?: UpdateInput, valueState?: InnerInputBaseState, callback?: () => void) {
    this.setState((prevstate: InnerInputState) => {
      let newState = {};
      if (state) {
        newState = { data: state };
      }

      if (valueState) {
        newState = Object.assign({}, newState, valueState);
      }
      return newState;
    }, callback);
  }

  focus() {
    if (this.element) {
      this.element.focus();
    }
  }

  blur() {
    if (this.element) {
      this.element.blur();
    }
  }

  private needKeyStopPropagation = (e: React.KeyboardEvent<HTMLElement>) => {
    // zachytávám použitou(již aplikovanou) klávesu, aby dále neprobublávala DOM stromem
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.keyCode === 37 && !e.altKey) {
      //ctrl+shift+left
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.keyCode === 39 && !e.altKey) {
      //ctrl+shift+right
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 67 && !(e.altKey || e.shiftKey)) {
      //ctrl+c
      e.stopPropagation();
    }
    if ((e.ctrlKey || e.metaKey) && e.keyCode === 45 && !(e.altKey || e.shiftKey)) {
      //ctrl+insert
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 86 && !(e.altKey || e.shiftKey)) {
      //ctrl+v
      e.stopPropagation();
    }
    if (e.shiftKey && e.keyCode === 45 && !(e.ctrlKey || e.shiftKey)) {
      //shift+insert
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 88 && !(e.altKey || e.shiftKey)) {
      //ctrl+x
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 65 && !(e.altKey || e.shiftKey)) {
      //ctrl+a
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 90 && !(e.altKey || e.shiftKey)) {
      //ctrl+z
      e.stopPropagation();
    }

    if ((e.ctrlKey || e.metaKey) && e.keyCode === 89 && !(e.altKey || e.shiftKey)) {
      //ctrl+y
      e.stopPropagation();
    }

    if (e.keyCode === 8 && !(e.altKey || e.shiftKey || e.ctrlKey)) {
      //Del
      e.stopPropagation();
    }

    if (e.keyCode === 46 && !(e.altKey || e.shiftKey || e.ctrlKey)) {
      //Del
      e.stopPropagation();
    }

    if (e.key === "-" || e.key === "+" || e.keyCode === 110 || e.key.match(/^[0-9]$/)) {
      // 110 = numpad decimal
      e.stopPropagation();
    }
  };

  private refCallBack = (element: HTMLInputElement | HTMLTextAreaElement) => {
    if (element) {
      this.element = element;
    }
  };

  componentDidUpdate(prevProps: WithContextPlacementProps, prevState: InnerInputStateBase) {
    if (this.element && this.state.Position != prevState.Position && this.state.Position >= 0) {
      setTimeout(() => {
        this.element.setSelectionRange(this.state.Position, this.state.Position);
      }, 10);
    }
  }

  render(): JSX.Element {
    let value: string = this.state.Value;
    let inputAttributes: InputHtmlAttributes = {
      type: "text",
      id: this.control.Ncl.Name,
      title: this.state.data.Hint + ": " + value,
      ...getAttributes(this.state.data),
    };
    let inputClass: string = this.props.renderer.renderRule(inputTag, { vcx: this.control.VCX }) + " " + this.props.className;

    inputAttributes.autoComplete = "off";
    inputAttributes.placeholder = this.props.InputTagPlaceholder;
    if (this.state.data.MaxLength) {
      inputAttributes.maxLength = this.state.data.MaxLength;
    }

    if (this.control.Ncl.FrgtData.IsPassword) {
      inputAttributes.type = this.props.revealPassword ? "text" : "password";
    }

    if (this.control.Ncl.InputType === TNclInputType.nitString && this.state.data.MultiLineMode) {
      return (
        <textarea
          className={inputClass}
          onKeyDown={this.handleKeyDown}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          ref={this.refCallBack}
          {...inputAttributes}
          value={value}
          spellCheck={false}
          rows={this.state.data.MultiLineMode == TInputMultiLineMode.imlmMultiLineSimple ? this.control.Ncl.FrgtData.Size : 0}
        />
      );
    } else {
      return (
        <input
          className={inputClass}
          value={value}
          ref={this.refCallBack}
          spellCheck={false}
          type="text"
          onKeyPress={this.props.onKeyPress}
          onKeyDown={this.handleKeyDown}
          onPaste={this.props.onPaste}
          onDrop={this.props.onDrop}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          {...inputAttributes}
        />
      );
    }
  }

  private handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (this.props.change) {
      let value = e.target.value;
      if (this.state.Value != value) {
        this.setState({ Value: value });
        this.props.change(value);
      }
    }
  };

  private handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (this.props.onKeyDown) {
      this.props.onKeyDown(e);
    }

    this.needKeyStopPropagation(e); // catched used key in input, that i can't use in other parts of application

    if (!e.isPropagationStopped()) {
      if (((e.ctrlKey || e.metaKey) && e.keyCode === 38) || ((e.ctrlKey || e.metaKey) && e.keyCode === 40)) {
        //ctrl+UpArrow//ctrl+DownArrow
        this.control.nextPriorValue(e.keyCode === 40 ? true : false, this.state.Value);
        e.preventDefault();
      }

      if ((e.shiftKey && e.key === "Enter") || (e.currentTarget.tagName === "TEXTAREA" && (e.key === "ArrowUp" || e.key === "ArrowDown"))) {
        e.stopPropagation();
      } else if (e.keyCode === 13) {
        //ENTER
        this.control.change(this.state.Value, false); //set value for send, but send key Enter
      }
    }
  };

  private handleFocus = (e: React.FocusEvent<any>) => {
    if (this.props.onFocus) {
      this.props.onFocus(e);
    }
    if (!(this.control.Parent instanceof NclLocatorPanel)) {
      this.element.select();
    }
  };

  private handleBlur = (e: React.FocusEvent<any>) => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
    setRequestedActiveControl(e, this.control.getRealizerUID());
    this.control.change(this.state.Value, true);
  };
}

const K2InnerInput = withContext(InnerInput);

interface InnerSimpleInputProps extends InnerInputProps, InputProps {
  showContextMenu?: () => void;
}

interface InnerSimpleInputState extends InnerInputStateBase {}

class InnerSimpleInput extends React.PureComponent<InnerSimpleInputProps, InnerSimpleInputState> {
  static displayName = `InnerSimpleInput`;
  private element: HTMLInputElement | HTMLTextAreaElement;
  private control: NclInput;
  private contextMenuTimer: any;
  private possibleShowContextMenu: boolean = false;
  private disableFocus: boolean; // zakazuje automaticky focus na input po zavolani metody setSelectionRange, stava se jen na iOS

  constructor(props: InnerSimpleInputProps) {
    super(props);
    this.control = AcquireControl(this.props.controlUID, this.props.vrUID, (ctrl) => {
      return ctrl instanceof NclInput;
    }) as NclInput;

    this.state = { data: this.control.State as UpdateInput, Value: "", vcxVersion: -1 };

    if (this.control.isDateOrTime() && this.control.ToolBar) {
      this.control.ToolBar.removeAction("_ExecuteLookupCOMMAND");
    }
  }

  updateState(state?: UpdateInput, valueState?: InnerInputBaseState) {
    this.setState((prevstate: InnerInputState) => {
      let newState = {};
      if (state) {
        newState = { data: state };
      }

      if (valueState) {
        newState = Object.assign({}, newState, valueState);
      }
      return newState;
    });
  }

  getOverRect(): DOMRect {
    if (this.element) {
      return this.element.getBoundingClientRect();
    }

    return null;
  }

  public get selectionStart(): number {
    return this.element ? this.element.selectionStart : -1;
  }

  public set selectionStart(value: number) {
    if (this.element) {
      this.element.selectionStart = value;
    }
  }

  public get selectionEnd(): number {
    return this.element ? this.element.selectionEnd : -1;
  }

  public set selectionEnd(value: number) {
    if (this.element) {
      this.element.selectionEnd = value;
    }
  }

  public get Value(): string {
    return this.state.Value;
  }

  focus() {
    if (!this.element) return;

    if (Context.DeviceInfo.ShowKeyboardOnFocus === "1") {
      this.element.focus();
    } else {
      this.element.readOnly = true;
      this.element.focus();

      if (Context.DeviceInfo.BrowserInfo !== "Firefox") {
        this.element.readOnly = false;
      }
    }
  }

  blur() {
    if (this.element) {
      this.element.blur();
    }
  }

  private getInputType(): string {
    switch (this.control.Ncl.InputType) {
      case TNclInputType.nitDateTime:
        return "datetime-local"; // datetime je deprecated
      case TNclInputType.nitDate:
        return "date";
      case TNclInputType.nitTime:
        return "time";
      case TNclInputType.nitNumeric:
        return "number";
      default:
        break;
    }

    return "text";
  }

  private getInputMode(): "numeric" | "decimal" | "text" {
    if (this.control.Ncl.InputType === TNclInputType.nitNumeric) {
      if (this.control.getDecimalsCountByFormat() > 0) {
        return "decimal";
      } else {
        return "numeric";
      }
    }

    return null; // Pokud se vrati typ inputmode = 'text', tak Enter na SW klavesnici se chova jako novy radek a ne jako potvrzeni, a neodesle se tak pozadavek na server; napr. odeslani hodnoty u sloupcoveho filtru.
  }

  private getValue(value: string) {
    if (value == undefined) return "";

    if (this.control.Ncl.InputType === TNclInputType.nitTime) {
      return this.control.getTimeByFormat(this.state.data.Format, value);
    } else {
      return value;
    }
  }

  private getStep(): string {
    if (this.control.Ncl.InputType == TNclInputType.nitNumeric) {
      return (1 / Math.pow(10, 4)).toString();
    }

    return "1";
  }

  componentDidUpdate(prevProps: WithContextPlacementProps, prevState: InnerInputStateBase) {
    if (this.element && this.state.Position != prevState.Position && this.state.Position >= 0) {
      setTimeout(() => {
        this.disableFocus = true;
        this.element.setSelectionRange(this.state.Position, this.state.Position);
        this.disableFocus = false;
      }, 10);
    }
  }

  private refCallBack = (ref: HTMLInputElement | HTMLTextAreaElement) => {
    if (ref) {
      this.element = ref;
      if ((Context.DeviceInfo.IsIOS || Context.DeviceInfo.IsIPadOS) && Context.DeviceInfo.IsTouchDevice && this.element instanceof HTMLInputElement) {
        this.element.defaultValue = "";
      }
    }
  };

  render(): JSX.Element {
    // iOS/iPadOS bug - https://github.com/facebook/react/issues/8938
    if (
      (Context.DeviceInfo.IsIOS || Context.DeviceInfo.IsIPadOS) &&
      Context.DeviceInfo.IsTouchDevice &&
      this.element instanceof HTMLInputElement &&
      this.control.isDateOrTime()
    ) {
      const target = this.element;
      setTimeout(() => {
        target.defaultValue = "";
      }, 100);
    }
    let value = this.getValue(this.state.Value);
    let inputAttributes: InputHtmlAttributes = {
      type: this.getInputType(),
      id: this.control.Ncl.Name,
      title: this.state.data.Hint + ": " + value,
      ...getAttributes(this.state.data),
    };
    let inputClass: string = this.props.renderer.renderRule(inputTag, { vcx: this.control.VCX }) + " " + this.props.className;

    inputAttributes.autoComplete = "off";
    inputAttributes.placeholder = this.props.InputTagPlaceholder;
    if (this.state.data.MaxLength) {
      inputAttributes.maxLength = this.state.data.MaxLength;
    }

    if (this.control.Ncl.FrgtData.IsPassword) {
      inputAttributes.type = this.props.revealPassword ? "text" : "password";
    }
    if (this.control.Ncl.InputType === TNclInputType.nitString && this.state.data.MultiLineMode) {
      return (
        <textarea
          className={inputClass}
          onFocus={this.handleFocus}
          onBlur={this.handleBlur}
          onChange={this.handleChange}
          onContextMenu={this.handleContextMenu}
          ref={this.refCallBack}
          {...inputAttributes}
          value={value}
          spellCheck={false}
          rows={this.state.data.MultiLineMode == TInputMultiLineMode.imlmMultiLineSimple ? this.control.Ncl.FrgtData.Size : 0}
          onKeyDown={this.handleKeyDown}
        />
      );
    } else {
      return (
        <div style={{ width: "100%", minWidth: this.control.VCX.sizeMap(20) + "px" }}>
          <input
            style={{ flex: 1 }}
            className={inputClass}
            ref={this.refCallBack}
            inputMode={this.getInputMode()}
            step={this.getStep()}
            value={value}
            onFocus={this.handleFocus}
            onBlur={this.handleBlur}
            onChange={this.handleChange}
            onPaste={this.props.onPaste}
            onTouchEnd={this.handleTouchEnd}
            onTouchStart={this.handleTouchStart}
            onTouchMove={this.handleTouchMove}
            //onTouchCancel={this.handleTouchCancel}
            onContextMenu={this.handleContextMenu}
            onKeyDown={this.handleKeyDown}
            {...inputAttributes}
          />
          {this.control.isDateOrTime() && (
            <K2Img
              strokeColor={this.control.VCX.getColor(this.control.VCX.Data.ColorMap.BaseColorBck1)}
              glyphId="wui*calendar"
              vcx={this.control.VCX}
              width={this.control.VCX.InputControl.getEditHeight(1)}
              height={this.control.VCX.InputControl.getEditHeight(1)}
            />
          )}
        </div>
      );
    }
  }

  private handleKeyDown = (e: React.KeyboardEvent) => {
    if (
      e.key === "Delete" ||
      e.key === "ArrowUp" ||
      e.key === "ArrowDown" ||
      e.key === "ArrowLeft" ||
      e.key === "ArrowRight" ||
      e.key === "z" ||
      e.key === "y" ||
      e.key === "-" ||
      e.keyCode === 110 || // numpad decimal
      e.key.match(/^[0-9]$/) ||
      e.key === "Backspace"
    ) {
      e.stopPropagation();
    }
  };

  private handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (this.props.change) {
      let value = e.target.value;
      switch (this.control.Ncl.InputType) {
        case TNclInputType.nitNumeric:
          if (this.state.Value === "0") {
            if (value.startsWith(this.state.Value)) {
              value = value.substr(1);
            } else if (value.endsWith(this.state.Value)) {
              value = value.substr(0, 1);
            }
          }
          let accept = this.control.checkInput(this.state.Value, value, { start: 0, end: 0 }, true);
          if (!value || (accept && accept.Result === EditCheckValueResult.newValue)) {
            if (this.state.Value != value) {
              this.props.change(value);
              this.setState({ Value: value });
            }
          }

          break;
        case TNclInputType.nitDateTime:
        case TNclInputType.nitDate:
        case TNclInputType.nitTime:
          if (this.state.Value != value && (value !== "" || (value === "" && !this.control.isEmpty()))) {
            this.props.change(value);
            this.control.accept();
          }
          break;
        case TNclInputType.nitString:
          if (this.state.Value != value) {
            this.props.change(value);
            this.setState({ Value: value });

            // Pokud je input typu MultiLineMode, vykresluje se jako <textarea>.
            // Na mobilech nelze poznat klavesu Enter, takze akci vyvolavam, pokud hodnota konci zalomenim radku.
            if (!this.isSingleLine() && this.endsWithEnter(value)) {
              this.control.appendFunction({ Name: cJSonFunctionExecuteShortcut, Args: ["ENTER"] }, true);
            }
          }
          break;
      }
    }
  };

  private isSingleLine = () => {
    if (this.state.data.MultiLineMode === TInputMultiLineMode.imlmSingleLine) {
      return true;
    }

    return false;
  };

  private endsWithEnter = (value: string) => {
    if (value.match(/\n$/)) {
      return true;
    }

    return false;
  };

  private handleTouchStart = (e: React.TouchEvent) => {
    if (Context.DeviceInfo.BrowserInfo === "Firefox") {
      this.element.readOnly = false;
    }

    this.possibleShowContextMenu = true;
    this.contextMenuTimer = setTimeout(() => {
      if (this.possibleShowContextMenu) {
        this.possibleShowContextMenu = false;
        if (this.props.showContextMenu) {
          this.props.showContextMenu();
        }
      }
    }, 610);
  };

  private handleTouchEnd = (e: React.TouchEvent) => {
    if (this.possibleShowContextMenu === false) {
      e.preventDefault();
    }
    this.possibleShowContextMenu = false;
    clearTimeout(this.contextMenuTimer);
  };

  private handleTouchMove = (e: React.TouchEvent) => {
    this.possibleShowContextMenu = false;
    clearTimeout(this.contextMenuTimer);
  };

  private handleTouchCancel = (e: React.TouchEvent) => {
    this.possibleShowContextMenu = false;
    clearTimeout(this.contextMenuTimer);
  };

  private handleContextMenu = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation(); //on android show context menu with copy/paste/...
  };

  private handleFocus = (e: React.FocusEvent<any>) => {
    if (this.disableFocus) return;

    if (this.props.onFocus) {
      this.props.onFocus(e);
    }

    if (!(this.control.Parent instanceof NclLocatorPanel)) {
      this.element.select();
    }

    if (this.props.blockSoftKeyboard === true) {
      this.element.readOnly = true;
      setTimeout(() => {
        this.element.inputMode = this.getInputMode();
        this.element.readOnly = false;
      }, 0);
    }
  };

  private handleBlur = (e: React.FocusEvent<any>) => {
    if (this.props.onBlur) {
      this.props.onBlur(e);
    }
    this.control.accept();
  };
}

const K2InnerSimpleInput = withContext(InnerSimpleInput);

export const K2Input = withContext(_Input);

export class ColumnBaseHelper {
  static getForegroundColor(formatting: CondFormattingStyle, isSelected: boolean, fixed: boolean, vcx: VisualContext): string {
    if (this.UseForegroundColor(formatting)) {
      return vcx.getColor(formatting.ForegroundColor);
    }
    return "inherit";
  }

  static getBackgroundColor(formatting: CondFormattingStyle, isSelectedColumn: boolean, fixed: boolean, vcx: VisualContext): string {
    let result: string = "inherit";
    if (isSelectedColumn) {
      result = vcx.getColor(vcx.Data.ColorMap.GridRulerColorBck);
    }

    if (this.UseBackgroundColor(formatting)) {
      if (isSelectedColumn) {
        result = VCXColorMap.rgbToHTMLColor(
          VCXColorMap.combineAlpha(vcx.getRGBColor(formatting.BackColor), vcx.getRGBColor(vcx.Data.ColorMap.GridRulerColorBck), 180)
        );
      } else {
        result = vcx.getColor(formatting.BackColor);
      }
    } else {
      if (fixed) {
        if (isSelectedColumn) {
          result = VCXColorMap.rgbToHTMLColor(
            VCXColorMap.combineAlpha(vcx.getRGBColor(vcx.Data.ColorMap.DataChangeROColorBck), vcx.getRGBColor(vcx.Data.ColorMap.GridRulerColorBck), 180)
          );
        } else {
          result = vcx.getColor(vcx.Data.ColorMap.DataChangeROColorBck);
        }
      }
    }

    return result;
  }
  static IsBold(formatting: CondFormattingStyle): boolean {
    return formatting && (formatting.Options & TCondFormattingOption.cfoFontStyleBold) === TCondFormattingOption.cfoFontStyleBold;
  }

  static IsItalic(formatting: CondFormattingStyle): boolean {
    return formatting && (formatting.Options & TCondFormattingOption.cfoFontStyleItalic) === TCondFormattingOption.cfoFontStyleItalic;
  }

  static IsStrike(formatting: CondFormattingStyle): boolean {
    return formatting && (formatting.Options & TCondFormattingOption.cfoFontStyleStrike) === TCondFormattingOption.cfoFontStyleStrike;
  }

  static IsUnderline(formatting: CondFormattingStyle): boolean {
    return formatting && (formatting.Options & TCondFormattingOption.cfoFontStyleUnderline) === TCondFormattingOption.cfoFontStyleUnderline;
  }

  static UseBackgroundColor(formatting: CondFormattingStyle): boolean {
    return formatting && formatting.BackColor > 0 && (formatting.Options & TCondFormattingOption.cfoBackColor) === TCondFormattingOption.cfoBackColor;
  }

  static UseForegroundColor(formatting: CondFormattingStyle): boolean {
    return (
      formatting &&
      formatting.ForegroundColor > 0 &&
      (formatting.Options & TCondFormattingOption.cfoForegroundColor) === TCondFormattingOption.cfoForegroundColor
    );
  }
}
